import { useCallback, useEffect, useRef, useState } from 'react'
import {
  clearPendingCheckoutId,
  readPendingCheckoutId,
  useBillingCheckoutSuccessPoll,
} from '@/stores/billing.store'
import type { CheckoutSelection } from './order-summary-model'

export type CheckoutStep = 'plan' | 'payment' | 'activating'
export type CheckoutPhase = 'idle' | 'confirming' | 'confirmed' | 'recovering'

interface UseCheckoutFlowOptions {
  organizationId: string
  onSuccess: () => void
  onPending?: (checkoutId: string) => void
}

export interface CheckoutFlowState {
  step: CheckoutStep
  phase: CheckoutPhase
  selection: CheckoutSelection | null
  pendingCheckoutId: string | null
  // How many times the user has explicitly clicked "Retry confirmation" for
  // the current pending checkout. Lets the recovery panel say something more
  // useful than "you can safely retry" once that has already failed.
  retryCount: number
  selectPlan: (selection: CheckoutSelection) => void
  continueToPayment: () => void
  beginActivation: () => void
  backToPlan: () => void
  completeCheckout: () => void
  markPending: (checkoutId: string) => void
  retryConfirmation: () => void
  startOver: () => void
}

/**
 * Step/phase machine behind CheckoutFlow. Owns plan selection, the plan →
 * payment advance, and the pending-checkout recovery poll lifted verbatim from
 * the old SubscribeCheckout so a redirect-based return still reconciles.
 *
 * Invariant: `pendingCheckoutId` short-circuits the whole flow into recovery,
 * so the recovery poll here and the payment-step poll inside
 * InlineCheckoutPanel are never active at the same time.
 */
export function useCheckoutFlow({
  organizationId,
  onSuccess,
  onPending,
}: UseCheckoutFlowOptions): CheckoutFlowState {
  const [selection, setSelection] = useState<CheckoutSelection | null>(null)
  const [step, setStep] = useState<CheckoutStep>('plan')
  const [pendingCheckoutId, setPendingCheckoutId] = useState(() => readPendingCheckoutId(organizationId))
  const [confirmationExhausted, setConfirmationExhausted] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const handledRecoverySuccessRef = useRef(false)
  const { outcome: recoveryOutcome, startPolling: startRecoveryPolling } =
    useBillingCheckoutSuccessPoll(organizationId)

  useEffect(() => {
    setPendingCheckoutId(readPendingCheckoutId(organizationId))
    setConfirmationExhausted(false)
    setRetryCount(0)
    handledRecoverySuccessRef.current = false
  }, [organizationId])

  useEffect(() => {
    if (!pendingCheckoutId || confirmationExhausted || recoveryOutcome !== 'idle') return
    startRecoveryPolling(pendingCheckoutId, 'redirect')
  }, [confirmationExhausted, pendingCheckoutId, recoveryOutcome, startRecoveryPolling])

  useEffect(() => {
    if (recoveryOutcome === 'succeeded' && !handledRecoverySuccessRef.current) {
      handledRecoverySuccessRef.current = true
      setPendingCheckoutId(null)
      setConfirmationExhausted(false)
      onSuccess()
    } else if (recoveryOutcome === 'stuck') {
      setConfirmationExhausted(true)
    }
  }, [onSuccess, recoveryOutcome])

  const selectPlan = useCallback((next: CheckoutSelection) => {
    setStep('plan')
    setSelection(next)
  }, [])

  const continueToPayment = useCallback(() => {
    setSelection((current) => {
      if (current) setStep('payment')
      return current
    })
  }, [])

  const beginActivation = useCallback(() => {
    setStep((current) => (current === 'payment' ? 'activating' : current))
  }, [])

  const backToPlan = useCallback(() => setStep('plan'), [])

  const completeCheckout = useCallback(() => {
    setStep('plan')
    setSelection(null)
    onSuccess()
  }, [onSuccess])

  const markPending = useCallback(
    (checkoutId: string) => {
      setStep('plan')
      setPendingCheckoutId(checkoutId)
      setConfirmationExhausted(true)
      setRetryCount(0)
      onPending?.(checkoutId)
    },
    [onPending]
  )

  const retryConfirmation = useCallback(() => {
    if (!pendingCheckoutId) return
    setConfirmationExhausted(false)
    setRetryCount((count) => count + 1)
    // An explicit retry reconciles immediately instead of polling for 30s
    // first - the user already knows something is stuck.
    startRecoveryPolling(pendingCheckoutId, 'redirect', true)
  }, [pendingCheckoutId, startRecoveryPolling])

  const startOver = useCallback(() => {
    clearPendingCheckoutId(organizationId)
    setPendingCheckoutId(null)
    setConfirmationExhausted(false)
    setRetryCount(0)
    setStep('plan')
  }, [organizationId])

  const phase: CheckoutPhase = pendingCheckoutId
    ? confirmationExhausted
      ? 'recovering'
      : 'confirming'
    : recoveryOutcome === 'succeeded'
      ? 'confirmed'
      : 'idle'

  return {
    step,
    phase,
    selection,
    pendingCheckoutId,
    retryCount,
    selectPlan,
    continueToPayment,
    beginActivation,
    backToPlan,
    completeCheckout,
    markPending,
    retryConfirmation,
    startOver,
  }
}
